import getRouteKey from 'common/src/modules/routing/utils/getRouteKey';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {RouteKey} from '../../../routing/routing.enums';

type BrandGuidelinesNavigationContext = {
  isOnBrandGuidelinesPage: boolean;
  currentTab: string | null;
  availableTabs: string[];
};

function brandGuidelinesNavigationContext(): AgentContext<BrandGuidelinesNavigationContext> {
  function getter(): BrandGuidelinesNavigationContext {
    const currentRouteKey = getRouteKey(window.location.pathname);
    const isOnBrandGuidelinesPage = currentRouteKey === RouteKey.BrandGuidelines;

    // Get current tab from URL search params
    let currentTab: string | null = null;
    const availableTabs = ['brand-guidelines', 'brand-assets', 'brand-personas'];

    if (isOnBrandGuidelinesPage) {
      const urlParams = new URLSearchParams(window.location.search);
      currentTab = urlParams.get('tabId') || 'brand-guidelines'; // Default to brand-guidelines tab
    }

    return {
      isOnBrandGuidelinesPage,
      currentTab,
      availableTabs,
    };
  }

  function formatter({
    prevContext,
    currentContext,
  }: {
    prevContext: BrandGuidelinesNavigationContext | null;
    currentContext: BrandGuidelinesNavigationContext;
  }): string | null {
    const {isOnBrandGuidelinesPage, currentTab, availableTabs} = currentContext;

    if (!isOnBrandGuidelinesPage) {
      if (prevContext?.isOnBrandGuidelinesPage) {
        return 'The user has left the Brand Guidelines page.';
      }

      return 'The user is currently not on the Brand Guidelines page.';
    }

    // User is on Brand Guidelines page
    const tabDisplayNames: Record<string, string> = {
      'brand-guidelines': 'Brand Guidelines',
      'brand-assets': 'Brand Assets',
      'brand-personas': 'Brand Personas',
    };

    const currentTabDisplayName = currentTab
      ? tabDisplayNames[currentTab] || currentTab
      : 'Brand Guidelines';

    // Check if tab changed
    if (prevContext?.isOnBrandGuidelinesPage && prevContext.currentTab !== currentTab) {
      const prevTabDisplayName = prevContext.currentTab
        ? tabDisplayNames[prevContext.currentTab] || prevContext.currentTab
        : 'Brand Guidelines';

      return `The user has switched from the ${prevTabDisplayName} tab to the ${currentTabDisplayName} tab on the Brand Guidelines page.`;
    }

    // First time on the page or no previous context
    const availableTabsText = availableTabs.map((tab) => tabDisplayNames[tab] || tab).join(', ');

    return `The user is currently on the Brand Guidelines page, viewing the ${currentTabDisplayName} tab. Available tabs: ${availableTabsText}.`;
  }

  return {
    title: 'Brand Guidelines Navigation',
    getter,
    formatter,
  };
}

export default brandGuidelinesNavigationContext;
